import { ProcessorType } from '@weave/schema-gen-ts/dist/schemas/payments/messages/values/values.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { InvoiceModel, PaymentOriginEnum } from '@frontend/api-invoices';
import { CreatePaymentIntentBody, makePaymentSync, PaymentMethodTypeEnum } from '@frontend/api-weave-pay';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { isPaidOrPendingInvoice } from '@frontend/payments-invoice-calculations';
import { genUUIDV4 } from '@frontend/string';
import { useAlert } from '@frontend/design-system';
import { FinanceOption } from '../types/care-credit.types';

export enum PaymentIntentSyncErrors {
  RequestAlreadyCompleted = 'request is already completed',
}

const getErrorCause = (err: unknown) => {
  let cause = '';
  if (err instanceof HttpError && err.data && typeof err.data !== 'string' && 'message' in err.data) {
    const errorMessage = JSON.parse(err.data.message);
    cause = errorMessage.cause ?? '';
  }
  return cause;
};
export interface CareCreditCreatePaymentIntentOptions {
  locationId: string | undefined;
  origin: PaymentOriginEnum;
  invoice: InvoiceModel | undefined;
  person: Person | undefined;
  selectedFinanceOption: Pick<FinanceOption, 'promoCode'>;
  // invoiceId is not required to use the makePaymentSync method, but it **is** required
  // to use the correct endpoint in `makePaymentSync`
  // https://github.com/weave-lab/frontend/blob/e9267e569fa6ead8af34b9b7bb3217433974ebe1/libs/apis/api-weave-pay/src/api.ts#L120
  invoiceId: string;
  receiptEmail?: string;
  throwErrorOnCreateFailed?: boolean;

  onInvoiceAlreadyPaid?: () => void;
  onRefetchInvoice?: () => Promise<InvoiceModel | undefined>;
}

export const useCareCreditCreatePaymentIntent = ({
  locationId,
  origin,
  invoice,
  person,
  invoiceId,
  receiptEmail,
  selectedFinanceOption,
  throwErrorOnCreateFailed,
  onInvoiceAlreadyPaid,
  onRefetchInvoice,
}: CareCreditCreatePaymentIntentOptions) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();

  const errorAlert = (message: string) =>
    alerts.error({
      autoDismissAfter: 12000,
      message,
    });

  const createPaymentIntent = async () => {
    if (!locationId) {
      console.error('the locationId is empty', locationId);
      return { paymentId: undefined, paymentIntent: undefined };
    }

    try {
      if (onRefetchInvoice) {
        const invoice = await onRefetchInvoice();
        if (isPaidOrPendingInvoice(invoice)) {
          throw new Error(PaymentIntentSyncErrors.RequestAlreadyCompleted);
        }
      }
      const paymentIntentInput: CreatePaymentIntentBody = {
        paymentId: genUUIDV4(),
        person: personV3ToPaymentIntentPerson(person, invoice),
        amount: invoice?.billedAmount,
        origin,
        paymentMethodType: PaymentMethodTypeEnum.CARD,
        receiptEmail,
        cardholderName: `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim(),
        processorMetadata: {
          processorType: ProcessorType.CARE_CREDIT_PROCESSOR,
          careCredit: {
            promoCode: selectedFinanceOption?.promoCode || '',
          },
        },
      };

      const {
        paymentId,
        paymentIntent,
        paymentStatus: status,
      } = await makePaymentSync(paymentIntentInput, locationId, invoiceId);

      return { paymentId, paymentIntent, status };
    } catch (err) {
      try {
        if (
          (err instanceof Error && err.message === PaymentIntentSyncErrors.RequestAlreadyCompleted) ||
          getErrorCause(err) === PaymentIntentSyncErrors.RequestAlreadyCompleted
        ) {
          errorAlert(t('Invoice is already paid.'));
          onRefetchInvoice?.();
          onInvoiceAlreadyPaid?.();
        } else {
          // throwing to show the generic error message in next catch block
          throw err;
        }
      } catch (err) {
        if (throwErrorOnCreateFailed) {
          // check if err.message is a json response. If yes, throw user-safe message instead.
          if (err instanceof HttpError && err.data && typeof err.data !== 'string') {
            console.error('Error creating payment intent. Backend response:', err.data);
            throw new Error(t('Failed to create payment. Please try again or use a different payment method.'));
          }
          throw err;
        }

        errorAlert(t('Error retrieving payment details.'));
      }
    }

    return { paymentId: undefined, paymentIntent: undefined };
  };

  return { createPaymentIntent };
};

function personV3ToPaymentIntentPerson(
  person: Person | undefined,
  invoice: InvoiceModel | undefined
): CreatePaymentIntentBody['person'] {
  if (!person) {
    return undefined;
  }

  return {
    id: person?.personId,
    firstName: person?.firstName,
    lastName: person?.lastName,
    homePhone: person?.contactInfo?.find((c) => c.type === ContactType_Enum.PHONE_HOME)?.destination,
    mobilePhone: person?.contactInfo?.find((c) => c.type === ContactType_Enum.PHONE_MOBILE)?.destination,
    email: person?.contactInfo?.find((c) => c.type === ContactType_Enum.EMAIL)?.destination,
    birthdate: person?.birthdate,
    pmId: invoice?.person?.pmid,
  };
}
